/**
 * NoteDisplay Component
 *
 * Renders note content with markdown support.
 */

import React from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

interface NoteDisplayProps {
  content: string;
  className?: string;
}

interface HeadingProps {
  children?: React.ReactNode;
}

// Notes are small cards: every heading level renders at one of two small
// sizes. BlockNote's slash menu offers Heading 1 first.
const MajorHeading = ({ children }: HeadingProps) => (
  <h3 className="text-sm font-semibold text-gray-900 mt-2 mb-1 first:mt-0">{children}</h3>
);

const MinorHeading = ({ children }: HeadingProps) => (
  <h4 className="text-sm font-semibold text-gray-800 mt-2 mb-1 first:mt-0">{children}</h4>
);

export const NoteDisplay: React.FC<NoteDisplayProps> = ({
  content,
  className = "",
}) => {
  return (
    <div className={`text-sm text-gray-800 prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 ${className}`}>
      <ReactMarkdown
        // remark-breaks: notes typed in a textarea used one newline per line (TD-092).
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
          h1: MajorHeading,
          h2: MajorHeading,
          h3: MinorHeading,
          h4: MinorHeading,
          h5: MinorHeading,
          h6: MinorHeading,
          ul: ({ children }) => (
            <ul className="list-disc pl-4 mb-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-4 mb-1">{children}</ol>
          ),
          // Per item, not per list: a list can mix bullets and tasks. The
          // checkbox takes the marker's place.
          li: ({ children, className }) => (
            <li className={className?.includes("task-list-item") ? "mb-0.5 list-none -ml-4" : "mb-0.5"}>
              {children}
            </li>
          ),
          input: ({ checked }) => (
            <input type="checkbox" checked={!!checked} readOnly disabled className="mr-1.5 align-middle" />
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {children}
            </a>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold">{children}</strong>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
